import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToken } from '../../auth/useToken';
import { useUser } from '../../auth/useUser';
import TopMenuBar from '../../components/TopMenuBar';
import LeftSidebar from '../../components/LeftSidebar';
import './UserInfoPage.css'
import AddHouseholdMemberModal from '../../components/MyPantry/AddHouseholdMemberModal';

export const UserInfoPage = () => {
  const user = useUser();
  const [token, setToken] = useToken();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const { id, email, info } = user || {};

  const [isVerified, setIsVerified] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPrimaryUser, setIsPrimaryUser] = useState(false);
  const [householdId, setHouseholdId] = useState(0)
  const [household, setHousehold] = useState('');
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [pantryItems, setPantryItems] = useState([]);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);

  useEffect(() => {
    if (showSuccessMessage || showErrorMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setShowErrorMessage(false);
      }, 3000);
      return () => clearTimeout(timer); // Cleanup timeout on component unmount
    }
  }, [showSuccessMessage, showErrorMessage]);

  const verifiedAccount = async () => {
    try {
      const response = await axios.get(`/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { isverified, phone_number, first_name, last_name, household_id, household } = response.data.user;

      setIsVerified(isverified);
      setPhoneNumber(phone_number);
      setFirstName(first_name);
      setLastName(last_name);
      setHouseholdId(household_id)
      setHousehold(household);
    } catch (e) {
      console.error('Error response:', e.response);
      setShowErrorMessage(true);
    }
  };
  
  useEffect(() => {
    verifiedAccount(); // Call the function on component load
  }, [id, token]); // Dependencies: run effect when `id` or `token` changes

  useEffect(() => {
    const fetchHouseholdMembers = async () => {
      if (householdId) {
        try {
          const response = await axios.get(`/api/household/${householdId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setHouseholdMembers(response.data.household);
        } catch (error) {
          console.error('Error fetching household members:', error);
        }
      }
    };

    fetchHouseholdMembers();
  }, [householdId, token]);

  useEffect(() => {
    const fetchPantryItems = async () => {
      if (household) {
        try {
          const response = await axios.get(`/api/Inventory`, {
            params: { household_id: household },
            headers: { Authorization: `Bearer ${token}` }
          });
          setPantryItems(response.data);
        } catch (error) {
          console.error('Error fetching pantry items:', error);
        }
      }
    };

    fetchPantryItems();
  }, [household, token]);

  const saveChanges = async () => {
    try {
        const response = await axios.put(`/api/users/${id}`, {
            phoneNumber,
            isPrimaryUser,
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });

        const { token: newToken } = response.data;
        
        setToken(newToken);
        setShowSuccessMessage(true);
    } catch (error) {
        console.error('Error response:', error.response); // Log full error response
        setShowErrorMessage(true);
    }
};

  const logOut = () => {
    localStorage.removeItem('token')
    navigate('/login'); // Redirect to login page
  };

  const resetValues = () => {
    setPhoneNumber(info?.phoneNumber || '');
    setIsPrimaryUser(info?.isPrimaryUser || false);
  };

  return (
    <>
    <TopMenuBar user={firstName} />
    <LeftSidebar />
      <div className="section-container">
        <div className="pantry-section">
          <div className="pantry-table-container">
            <div className="pantry-header">
              <h2>Your Pantry</h2>
              <h4>20 Available Recipes - 40 Other Recipes Missing 1 Ingredient</h4>
              <button className="add-item-button">
                <span>+</span> Add Item
              </button>
            </div>
            <table className="pantry-items">
              <thead>
                <tr>
                  <th>Ingredient Name</th>
                  <th>Quantity</th>
                  <th>Category</th>
                  <th>Sub-Category</th>
                  <th>Expiration Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pantryItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.ingredient_name}</td>
                    <td>{item.quantity} { item.unit_name === 'N/A' ? "": item.unit_name }</td>
                    <td>-</td>
                    <td>-</td>
                    <td>{item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : '-'}</td>
                    <td>
                      <button className="edit-item-button">
                        Edit
                      </button>
                      <button className="remove-item-button">
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="expiration-warnings">
            <h3>Expiration Warnings</h3>
            <div className="warning-items">
              {pantryItems
                .filter(item => {
                  if (!item.expiration_date) return false;
                  const expirationDate = new Date(item.expiration_date);
                  const today = new Date();
                  const sevenDaysFromNow = new Date();
                  sevenDaysFromNow.setDate(today.getDate() + 7);
                  return expirationDate <= sevenDaysFromNow;
                })
                .map((item, index) => {
                  const expirationDate = new Date(item.expiration_date);
                  const today = new Date();
                  const isExpired = expirationDate < today;
                  return (
                    <div key={index} className={`warning-item ${isExpired ? 'expired' : 'expiring-soon'}`}>
                      <span className="item-name">{item.ingredient_name}</span>
                      <span className="expiration-date">
                        {isExpired 
                          ? 'Expired on ' 
                          : 'Expires on '} 
                        {expirationDate.toLocaleDateString()}
                      </span>
                    </div>
                  );
                })}
              {pantryItems.filter(item => {
                if (!item.expiration_date) return false;
                const expirationDate = new Date(item.expiration_date);
                const today = new Date();
                const sevenDaysFromNow = new Date();
                sevenDaysFromNow.setDate(today.getDate() + 7);
                return expirationDate <= sevenDaysFromNow;
              }).length === 0 && (
                <p className="no-warnings">No items are expired or expiring soon.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="section-container">
        <div className="Household">
          { household ?
          <>
          <div className="household-header">
            <h2>{household} Household Members</h2>
            <button 
              className="add-member-button"
              onClick={() => setIsAddMemberModalOpen(true)}
            >
              <span>+</span> Add Member
            </button>
          </div>
            <table className="household-members">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Primary User</th>
                </tr>
              </thead>
              <tbody>
                {householdMembers.map((member, index) => (
                  <tr key={index}>
                    <td>{member.first_name}</td>
                    <td>{member.last_name}</td>
                    <td>{member.isPrimaryUser ? 'Yes' : 'No'}</td>
                    <td>
                      <button className="remove-member-button">
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
          :
          <>
            <h2>HouseHold</h2>
            <p>A household name is not created. You're unable to invite household members until a hosehold name is assigned.</p>
          </>
          }
        </div>
      </div>

      <div className="RecipePoll">
        <div className="recipe-poll-header">
          <h2>Recipe Poll</h2>
          <button className="create-poll-button">
            <span>+</span> Create Recipe Poll
          </button>
        </div>
        <div className="recipe-poll-content">
          <p>Create a poll to decide what to cook with your household members!</p>
          {/* Poll list will be implemented later */}
          <div className="no-polls-message">
            No active polls at the moment.
          </div>
        </div>
      </div>

      <AddHouseholdMemberModal 
        isOpen={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
      />
    </>
  );
};
